
import Vue, { PropType } from 'vue'
import {
    createCallbotCommandKey,
    attachCallbotMaterial,
    getCallbotMaterials,
    updateCallbotRunSettings,
} from '@/api/callbot'
import { CallbotConfiguration, CallbotMaterial, CallbotRun } from '@/types/callbot'
import { formatMaterialDuration, formatMaterialCount, RETRY_INTERVALS, REDIAL_OPTIONS } from './audioLibrary'

type Slot = 'morning' | 'day' | 'evening'

export default Vue.extend({
    name: 'EditCallbotDialog',
    props: {
        value: { type: Boolean, default: false },
        projectId: { type: String, required: true },
        run: { type: Object as PropType<CallbotRun | null>, default: null },
        configuration: {
            type: Object as PropType<CallbotConfiguration | null>,
            default: null,
        },
    },
    data() {
        return {
            redialOptions: REDIAL_OPTIONS,
            step: 0,
            saving: false,
            savingLabel: 'Сохраняем…',
            error: '',
            materials: [] as CallbotMaterial[],
            materialsLoading: false,
            materialId: null as string | null,
            initialMaxAttempts: 0,
            stepTitles: ['Аудитория', 'Сообщение', 'Связь и уведомления', 'Расписание'],
            slotOptions: [
                { value: 'morning' as Slot, title: 'Утро', time: '09:00–12:00' },
                { value: 'day' as Slot, title: 'День', time: '12:00–17:00' },
                { value: 'evening' as Slot, title: 'Вечер', time: '17:00–20:00' },
            ],
            form: {
                name: '',
                transferPhone: '',
                notificationConnectorId: '',
                scheduleOption: 'day' as Slot | 'custom',
                customFrom: '10:00',
                customTo: '14:00',
                maxAttempts: 1,
                retryIntervalMinutes: 10,
                moduleAEnabled: true,
                moduleAReminderMinutes: 20,
            },
        }
    },
    computed: {
        // У ранее запущенной рассылки мог остаться интервал не из списка —
        // показываем его, чтобы поле не оказалось пустым.
        retryIntervals(): number[] {
            const current = Number(this.form.retryIntervalMinutes)
            return RETRY_INTERVALS.includes(current)
                ? RETRY_INTERVALS
                : [...RETRY_INTERVALS, current].sort((a, b) => a - b)
        },
        notificationChannels(): CallbotConfiguration['notificationChannels'] {
            return this.configuration ? this.configuration.notificationChannels : []
        },
        audienceLabel(): string {
            if (!this.run) return '—'
            const activation = this.run.activation && this.run.activation.name
            const audience = this.run.audience && this.run.audience.name
            return activation || audience || 'Аудитория'
        },
        approvedMaterials(): CallbotMaterial[] {
            return this.materials.filter(material => material.status === 'approved')
        },
        selectedMaterial(): CallbotMaterial | null {
            return this.approvedMaterials.find(material => material.id === this.materialId) || null
        },
        currentMaterialId(): string | null {
            return (this.run && this.run.audio && this.run.audio.materialId) || null
        },
        lowersAttempts(): boolean {
            return (
                this.initialMaxAttempts > 0 &&
                this.form.maxAttempts < this.initialMaxAttempts
            )
        },
    },
    watch: {
        value(opened: boolean) {
            if (opened) {
                this.reset()
                this.loadMaterials()
            }
        },
    },
    methods: {
        materialCount: formatMaterialCount,
        reset() {
            const run = this.run
            this.step = 0
            this.error = ''
            this.saving = false
            this.materialId = (run && run.audio && run.audio.materialId) || null
            if (!run) return
            const schedule = run.schedule
            this.form = {
                name: run.name,
                transferPhone: run.transferPhone || '',
                notificationConnectorId: run.notification
                    ? run.notification.connectorId
                    : '',
                scheduleOption:
                    schedule && schedule.customWindow
                        ? 'custom'
                        : ((schedule && schedule.slots && schedule.slots[0]) || 'day'),
                customFrom:
                    schedule && schedule.customWindow ? schedule.customWindow.from : '10:00',
                customTo:
                    schedule && schedule.customWindow ? schedule.customWindow.to : '14:00',
                maxAttempts: (schedule && schedule.maxAttempts) || 1,
                retryIntervalMinutes: (schedule && schedule.retryIntervalMinutes) || 10,
                moduleAEnabled: run.moduleA ? run.moduleA.enabled : true,
                moduleAReminderMinutes: run.moduleA ? run.moduleA.reminderMinutes : 20,
            }
            this.initialMaxAttempts = this.form.maxAttempts
        },
        async loadMaterials() {
            this.materialsLoading = true
            const result = await getCallbotMaterials(this.projectId)
            this.materialsLoading = false
            this.materials = result.data || []
        },
        materialMeta(material: CallbotMaterial): string {
            return `${formatMaterialDuration(material.durationSeconds)} · ${material.fileName}`
        },
        async save() {
            if (!this.run) return
            this.saving = true
            this.error = ''
            const channel = this.notificationChannels.find(
                item => item.connectorId === this.form.notificationConnectorId
            )
            // Сначала настройки, потом аудио: валидация настроек падает
            // несравнимо чаще, и при таком порядке неуспех оставляет
            // рассылку нетронутой.
            this.savingLabel = 'Сохраняем настройки…'
            const settings = await updateCallbotRunSettings({
                projectId: this.projectId,
                runId: this.run.runId,
                name: this.form.name,
                slots:
                    this.form.scheduleOption === 'custom'
                        ? []
                        : [this.form.scheduleOption as Slot],
                customWindow:
                    this.form.scheduleOption === 'custom'
                        ? { from: this.form.customFrom, to: this.form.customTo }
                        : undefined,
                maxAttempts: this.form.maxAttempts,
                retryIntervalMinutes: this.form.retryIntervalMinutes,
                transferPhone: this.form.transferPhone,
                notification: channel
                    ? { connectorId: channel.connectorId, channelType: channel.type }
                    : null,
                moduleA: {
                    enabled: this.form.moduleAEnabled,
                    reminderMinutes: this.form.moduleAReminderMinutes,
                },
            })
            if (settings.error || !settings.data) {
                this.saving = false
                this.error = 'Не удалось сохранить настройки'
                return
            }
            // Ролик прикрепляется, только если выбран другой: у старых рассылок
            // запись загружена файлом и в библиотеке её нет.
            if (this.selectedMaterial && this.selectedMaterial.id !== this.currentMaterialId) {
                this.savingLabel = 'Подключаем аудиоролик…'
                const uploaded = await attachCallbotMaterial(
                    this.projectId,
                    this.run.runId,
                    this.selectedMaterial.id,
                    createCallbotCommandKey()
                )
                if (uploaded.error) {
                    this.saving = false
                    this.error = 'Настройки сохранены, аудиоролик заменить не удалось'
                    this.$emit('saved', settings.data)
                    return
                }
            }
            this.saving = false
            this.$emit('saved', settings.data)
            this.$emit('input', false)
        },
        close() {
            if (this.saving) return
            this.$emit('input', false)
        },
    },
})
